/**
 * Planner shared constants + pure helpers — one visual language across
 * Week / Day / Month zooms and the chip drawer.
 * Design source of truth: planning/preproduction-planner-mockup.html
 */

export const WC_COLOURS = {
    'Metal': '#2a78d6',
    'Wood': '#eb6834',
    'Spray-finish': '#4a3aa7',
    'Mount': '#eda100',
    'Glazing': '#1baf7a',
    'Fitup': '#008300',
    'Acrylic': '#e34948',
    'Print': '#e87ba4',
};

export const WC_ICONS = {
    'Metal': 'pl-metal',
    'Wood': 'pl-wood',
    'Spray-finish': 'pl-spray',
    'Mount': 'pl-mount',
    'Glazing': 'pl-glazing',
    'Fitup': 'pl-fitup',
    'Acrylic': 'pl-acrylic',
    'Print': 'pl-print',
};

export const NEUTRAL = '#6b6a66';

export function fmtMins(m) {
    if (m >= 60) {
        const h = Math.floor(m / 60), r = Math.round(m % 60);
        return r ? h + 'h ' + String(r).padStart(2, '0') : h + 'h';
    }
    return Math.round(m) + 'm';
}

export function fmtSecs(s) {
    return fmtMins(Math.round(s / 60));
}

export function fmtMoney(pence) {
    return '£' + Math.round(pence / 100).toLocaleString('en-GB');
}

export function bandClass(pct) {
    if (pct <= 50) return 'pl-b-low';
    if (pct <= 100) return 'pl-b-ok';
    if (pct <= 150) return 'pl-b-tight';
    return 'pl-b-over';
}

/* the CMS day-% convention: efficiency-adjusted */
export function adjustedPct(planned, capacity, efficiencyPercent) {
    if (capacity <= 0) return 0;
    const raw = (planned / capacity) * 100;
    const eff = efficiencyPercent || 100;
    return Math.round(raw * (100 + (100 - eff)) / 100);
}

export function initials(name) {
    return String(name || '').split(/\s+/).map(w => w[0]).join('').substr(0, 2).toUpperCase();
}

export function ymd(d) {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

/* "Tue 22 Sep" — the planner's short day everywhere a date is spoken */
export function fmtDay(date) {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

export function errMsg(err) {
    if (err.response && err.response.data) {
        return err.response.data.error || err.response.data.message || ('Request failed (' + err.response.status + ')');
    }
    return 'Request failed — check your connection';
}

/* One SortableJS feel across every planner drag surface (week grid,
   tray, assign shelf, day columns). forceFallback swaps the browser's
   native HTML5 drag — which won't start on touch, aborts when the DOM
   re-renders under it, and drops by cursor hotspot rather than the
   visible chip (how drops landed on the wrong person) — for Sortable's
   own mouse/touch drag: the clone tracks the pointer 1:1 and autoscroll
   works. Spread onto <draggable> via v-bind; extend `filter` per view. */
export const DRAG_OPTS = {
    forceFallback: true,
    fallbackOnBody: true,     // clone escapes overflow:auto containers
    fallbackTolerance: 6,     // px of wobble that still counts as a click
    animation: 150,
    delay: 150,               // touch: hold to lift, so a swipe still scrolls
    delayOnTouchOnly: true,
    touchStartThreshold: 6,
    scrollSensitivity: 80,
    scrollSpeed: 12,
    filter: '.pl-no-drag',
    preventOnFilter: false,
};

/* board-scoped lookups, built once per fetch */
export function wcLookups(workcentres) {
    const byId = {};
    (workcentres || []).forEach(wc => { byId[wc.id] = wc; });
    return {
        name: id => (byId[id] ? byId[id].name : null),
        order: id => (byId[id] ? byId[id].order : null),
        concurrent: id => (byId[id] ? !!byId[id].concurrent : false),
        colour: (id, type) => {
            if (type && type !== 'process') return NEUTRAL;
            return WC_COLOURS[byId[id] ? byId[id].name : ''] || NEUTRAL;
        },
        icon: (id, type) => {
            if (type === 'delivery') return 'pl-van';
            if (type === 'adhoc') return 'pl-wrench';
            return WC_ICONS[byId[id] ? byId[id].name : ''] || 'pl-misc';
        },
    };
}

// Production sheet target for an item — always the scribble canvas
// (view, scribble, add dims). iPads used to be diverted to the flat
// image view, but the canvas has been touch-friendly since Feb 2026
// (440606d) and the static image reads as "just a PDF" on the shop
// floor (Peter, 2026-07-28).
export function productionSheetUrl(itemId) {
    return '/item/' + itemId + '/production-sheet/scribble-canvas';
}

// Shorten from the MIDDLE, never the end. Items on one quote routinely
// differ only in their tail — "Oak dark stain 472 x 335 external size"
// vs "... 472 x 635" — so an end-cut label renders two different items
// identically and sends someone to the wrong sheet.
export function shortenMiddle(text, max = 46) {
    const s = String(text || '');
    if (s.length <= max) return s;
    const head = Math.ceil((max - 1) * 0.6);
    const tail = max - 1 - head;
    return s.slice(0, head).trim() + '…' + s.slice(s.length - tail).trim();
}

// One label for an item everywhere it's offered as a production sheet —
// Gate 2, the planner job panel — so the same item reads the same way on
// every surface. Position prefix is the only collision-proof part (names
// repeat within a quote), so it appears whenever there's more than one.
export function itemLabel(item, index, total) {
    if (!item) return 'Unassigned';
    const name = item.name || ('Item ' + item.id);
    const qty = item.quantity > 1 ? item.quantity + '× ' : '';
    const pos = (total > 1 && index !== null && index !== undefined) ? (index + 1) + '. ' : '';
    return pos + qty + shortenMiddle(name);
}

// Untruncated, for title= — the only way to read a long name on hover.
export function itemLabelFull(item) {
    if (!item) return 'Work not tied to a specific item';
    const name = item.name || ('Item ' + item.id);
    return (item.quantity > 1 ? item.quantity + '× ' : '') + name;
}

// Copy helper that also works on plain-http intranet hosts, where
// navigator.clipboard is unavailable (secure contexts only).
export function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text);
    }
    return new Promise((resolve, reject) => {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try {
            if (document.execCommand('copy')) { resolve(); } else { reject(new Error('copy failed')); }
        } catch (e) {
            reject(e);
        } finally {
            document.body.removeChild(ta);
        }
    });
}
